import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { StatusPill } from "@/admin/ui";

function CardTile({ card, i }) {
    return (
        <div
            data-testid={`overview-card-${card.key}`}
            className="glass rounded-3xl p-6 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.28)] transition-all duration-500"
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                    {card.label}
                </span>
                <span className="font-mono-alt text-[10px] text-violet-500">
                    /{String(i + 1).padStart(2, "0")}
                </span>
            </div>
            <div className="mt-4 font-display text-4xl font-medium text-black tracking-tight leading-none">
                {card.value}
            </div>
            <div className="mt-2 text-[12px] text-neutral-500">{card.hint}</div>
        </div>
    );
}

export default function AdminOverview() {
    const { data, loading, error } = useResource("/admin/overview");

    return (
        <div data-testid="admin-overview-page">
            <PageHeader
                title="Overview"
                subtitle="A live snapshot of every company, user, and AI agent across the CEO's GPT platform."
            />

            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-6">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="glass rounded-3xl p-6 animate-pulse h-[130px]" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data?.cards?.map((c, i) => (
                        <CardTile key={c.key} card={c} i={i} />
                    ))}
                </div>
            )}

            <div className="mt-10 grid lg:grid-cols-[1.6fr_1fr] gap-5">
                <div data-testid="overview-recent" className="glass rounded-3xl p-6 sm:p-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                                Recent Activity
                            </div>
                            <div className="mt-1 font-display text-xl text-black">Audit stream</div>
                        </div>
                        <a href="/admin/audit-logs" className="text-[12px] text-violet-600 hover:underline">
                            View all →
                        </a>
                    </div>
                    <ul className="mt-5 divide-y divide-black/[0.05]">
                        {(data?.recent_activity || []).map((r) => (
                            <li key={r.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                    <div className="font-medium text-black truncate">
                                        {prettyAction(r.action)}
                                    </div>
                                    <div className="text-[12px] text-neutral-500 truncate">
                                        {r.user} · {r.module}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <StatusPill status={r.status} />
                                    <span className="text-[11px] text-neutral-400">{friendlyTime(r.timestamp)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="glass rounded-3xl p-6 sm:p-8" data-testid="overview-health">
                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                        System Health
                    </div>
                    <div className="mt-1 font-display text-xl text-black">Live status</div>
                    <div className="mt-5 space-y-3">
                        {Object.entries(data?.system_health || {}).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                                <span className="capitalize text-neutral-700">{k.replace(/_/g, " ")}</span>
                                <StatusPill status={v} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function prettyAction(a) {
    if (!a) return "Action";
    return a.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
